import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { InputComponent } from '../../input';

import styles from './form-create-receive.module.css';
import { ButtonComponent } from '../../Button';
import {
  CreateIncomeDTO,
  createIncomeSchema,
} from '../../../utils/DTOs/createIncomeDTO';
import { createExpense } from '../../../../../model';
import { getTransactionCodesController } from '../../../../../controller';
import {
  TransactionCodeResponse,
  TransactionResponse,
} from '../../../../../interfaces';
import { CurrencyInput } from '../../CurrencyInput';
import { handleFormatValueInput } from '../../../utils/formatCurrencyInput';

interface CreateReceiveProps {
  onClose?: () => void;
  type: 'income' | 'expense';
  onCreate?: (transaction: TransactionResponse) => void;
  onCancel?: () => void;
}

export const CreateReceiveForm = ({
  onClose,
  type,
  onCreate,
  onCancel,
}: CreateReceiveProps) => {
  const [transactionCodes, setTransactionsCode] = useState<
    TransactionCodeResponse[]
  >([]);

  const today = new Date().toLocaleDateString().split('/').reverse().join('-');
  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateIncomeDTO>({
    resolver: yupResolver(createIncomeSchema),
    defaultValues: {
      type,
      value: '0,00',
      discharge_date: today,
      due_date: today,
    },
  });

  async function onSubmit(data: CreateIncomeDTO) {
    // http call post
    const res = await createExpense(data);
    if (res) {
      onCreate?.(res);
    }
    if (onClose) onClose();
  }

  const fetchTransactionCodes = useCallback(async () => {
    const response = await getTransactionCodesController();
    if (response) {
      setTransactionsCode(response);
    }
  }, []);

  useEffect(() => {
    if (type === 'expense') fetchTransactionCodes();
  }, [fetchTransactionCodes, type]);

  const handleDueDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const now = new Date();
      const currentDate = new Date(e.target.value);

      if (now.getTime() !== currentDate.getTime())
        setValue('discharge_date', '');
    },
    [setValue],
  );

  watch('transaction_code_id');

  return (
    <form
      onSubmit={handleSubmit(data => onSubmit(data))}
      className={styles.form}
    >
      <InputComponent
        label="Descrição"
        {...register('description')}
        variant={errors?.description?.message ? 'danger' : 'default'}
        hint={errors?.description?.message}
      />
      <div className={styles.row}>
        <InputComponent
          label="Vencimento"
          type="date"
          {...register('due_date')}
          onChange={handleDueDateChange}
          hint={errors.due_date?.message}
        />
        <InputComponent
          label="Baixa"
          type="date"
          {...register('discharge_date')}
          hint={errors.discharge_date?.message}
        />
      </div>
      <Controller
        control={control}
        name="value"
        render={({ field: { value, onChange, onBlur } }) => (
          <CurrencyInput
            value={value}
            onChange={val => {
              const currentValue = getValues('value');
              const formattedValue = handleFormatValueInput(val, currentValue);
              setValue('value', formattedValue, { shouldValidate: true });
              onChange(formattedValue);
            }}
            onBlur={onBlur}
            error={errors.value?.message?.toString()}
            label="Valor"
          />
        )}
      />

      {type === 'expense' && (
        <select
          {...register('transaction_code_id')}
          defaultChecked
          defaultValue=""
          className={`${styles.select} ${
            getValues('transaction_code_id') === '' ? styles.default : ''
          }`}
        >
          <option value="">Informar plano de conta</option>
          {transactionCodes.map(transactionCode => {
            const { code, name, id_transaction_code } = transactionCode;
            return (
              <option value={id_transaction_code} key={id_transaction_code}>
                {code}-{name}
              </option>
            );
          })}
        </select>
      )}

      <div className={styles.row}>
        <ButtonComponent text="Cancelar" variant="outline" onClick={onCancel} />
        <ButtonComponent text="Criar Conta" isLoading={isSubmitting} />
      </div>
    </form>
  );
};
